import { useMutation, useQueryClient } from '@tanstack/react-query'
import { assert } from '@/utils'
import { IBCTransferStatus, traceIbcStatus2 } from '@/wallet-utils'
import { queryKeys } from '@/query-keys'
import { MutationParameters } from './types'
import { useStrideChain } from '@/contexts/wallet'

export interface TraceIbcStatusMutationReturnType {
  status: IBCTransferStatus
}

// [sign/broadcast] mutations and [trace] mutation are a 2-part process.
// First, if [sign/broadcast] doesn't succeed within the UI time limit, [trace] mutation will continue
// the [tracing] process so user can continue when the ibc transfer completes if they haven't closed the modal yet.
// We can use the `isLoading` status provided by [trace] mutation to display the "Return Later" screen.
const useTraceIbcTransferMutation = ({ withdrawTransaction }: MutationParameters) => {
  const client = useQueryClient()

  const { address: strideAddress } = useStrideChain()

  const handleMutation = async (): Promise<TraceIbcStatusMutationReturnType> => {
    assert(strideAddress, 'Unable to trace ibc status of withdraw transaction without connecting your wallet.')

    assert(withdrawTransaction, 'Unable to trace ibc status of withdraw transaction while transaction is empty.')

    const status = await traceIbcStatus2({ denom: 'STRD', tx: withdrawTransaction }, { timeout: false })

    await client.invalidateQueries({
      queryKey: queryKeys.transactionHistoryIbcStatusByHash({ hash: withdrawTransaction.transactionHash })
    })

    await client.invalidateQueries({ queryKey: queryKeys.chainBalancesBase })

    return { status }
  }

  return useMutation({
    mutationFn: handleMutation
  })
}

export { useTraceIbcTransferMutation }
